/* ═══════════════════════════════════════════════════════════════
   TaskService — Task CRUD, assignments, comments, links, attachments,
   notifications via EC2 API (cloud, in-memory cache)
   ═══════════════════════════════════════════════════════════════ */

import type { Task, TaskAssignment, TaskComment, TaskNotification, TaskLink, TaskAttachment, TaskProjectLink } from '../../shared/types';
import * as cache from './cloudCache';
import type { TableOpts } from './cloudCache';

const taskOpts: TableOpts = { jsonFields: ['tags'] };

export const TaskService = {
  // ─── Tasks ───
  listTasks: async (): Promise<Task[]> => cache.list<Task>('tasks', taskOpts),
  saveTask: async (task: Task): Promise<Task> => cache.save('tasks', task, taskOpts),
  deleteTask: async (id: string): Promise<void> => cache.remove('tasks', id),

  // ─── Task Assignments ───
  listTaskAssignments: async (taskId?: string): Promise<TaskAssignment[]> => {
    if (taskId) return cache.query<TaskAssignment>('task_assignments', { taskId });
    return cache.list<TaskAssignment>('task_assignments');
  },
  saveTaskAssignment: async (a: TaskAssignment): Promise<TaskAssignment> => cache.save('task_assignments', a),
  deleteTaskAssignment: async (id: string): Promise<void> => cache.remove('task_assignments', id),

  // ─── Task Comments ───
  listTaskComments: async (taskId: string): Promise<TaskComment[]> => {
    return cache.query<TaskComment>('task_comments', { taskId });
  },
  saveTaskComment: async (c: TaskComment): Promise<TaskComment> => cache.save('task_comments', c),

  // ─── Task Notifications ───
  listTaskNotifications: async (memberId?: string): Promise<TaskNotification[]> => {
    if (memberId) return cache.query<TaskNotification>('task_notifications', { teamMemberId: memberId });
    return cache.list<TaskNotification>('task_notifications');
  },
  saveTaskNotification: async (n: TaskNotification): Promise<TaskNotification> => cache.save('task_notifications', n),

  // ─── Task Links ───
  listTaskLinks: async (taskId: string): Promise<TaskLink[]> => {
    return cache.query<TaskLink>('task_links', { taskId });
  },
  saveTaskLink: async (l: TaskLink): Promise<TaskLink> => cache.save('task_links', l),
  deleteTaskLink: async (id: string): Promise<void> => cache.remove('task_links', id),

  // ─── Task Attachments ───
  listTaskAttachments: async (taskId: string): Promise<TaskAttachment[]> => {
    return cache.query<TaskAttachment>('task_attachments', { taskId });
  },
  saveTaskAttachment: async (a: TaskAttachment): Promise<TaskAttachment> => cache.save('task_attachments', a),
  deleteTaskAttachment: async (id: string): Promise<void> => cache.remove('task_attachments', id),

  // ─── Task ↔ Project Links ───
  listTaskProjectLinks: async (taskId?: string): Promise<TaskProjectLink[]> => {
    if (taskId) return cache.query<TaskProjectLink>('task_project_links', { taskId });
    return cache.list<TaskProjectLink>('task_project_links');
  },
  saveTaskProjectLink: async (l: TaskProjectLink): Promise<TaskProjectLink> => cache.save('task_project_links', l),
  deleteTaskProjectLink: async (id: string): Promise<void> => cache.remove('task_project_links', id),

  // ─── File dialog (needs main process) ───
  openFileDialog: async (): Promise<{ data: string; fileName: string; mimeType: string } | null> => {
    return (window as any).api.openExpenseFileDialog();
  },
};
